import React, { useMemo, useState } from "react";
import { ArrowUpRight, Gavel, MessagesSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { UserAvatar } from "@/components/common";
import { SectionLabel } from "@/components/canvas/LosBits";
import { PersonChip } from "@/components/team/TeamBits";
import { useResource } from "@/hooks/use-resource";
import { ago } from "@/lib/sk";
import { ThreadStatus } from "@/components/meldinger/bits";

/** Notes written before threads existed, anchored to this item (or one of its sub-items). Read-only history. */
export const notesForItem = (project, item, withChildren = false) => {
  const ids = new Set([item.id, ...(withChildren ? (item.children || []).map((c) => c.id) : [])]);
  return (project?.messages || []).filter((m) => m.role === "note" && ids.has(m.anchor?.item_id));
};

/** The threads about this post – the same threads Meldinger and the sidebar show. */
export function useItemThreads(project, item) {
  const { data } = useResource(project?.id ? `/projects/${project.id}/threads` : null, null, { interval: 45000, enabled: Boolean(project?.id) });
  return useMemo(() => {
    const ids = new Set([item.id, ...(item.children || []).map((c) => c.id)]);
    const threads = (data?.threads || []).filter((t) => !t.read_only && (t.anchors?.item_ids || []).some((a) => ids.has(a.id)));
    const notes = notesForItem(project, item, true);
    return { threads, notes, count: threads.length + (notes.length ? 1 : 0), unread: threads.filter((t) => t.unread).length };
  }, [data, project, item]);
}

/** One legacy note, compact. Decisions are marked – and marked as *not applied*. */
export function NoteLine({ m, showAnchor = false, testId }) {
  const a = m.author || {};
  const decision = m.kind === "beslutning";
  return (
    <li className={cn("flex items-start gap-2 rounded-[10px] border px-2.5 py-2", decision ? "border-[hsl(var(--navy)/0.3)] bg-[hsl(var(--navy)/0.04)]" : "border-black/[0.06] bg-white")} data-testid={testId} data-kind={m.kind || "notat"}>
      <UserAvatar name={a.name} initials={a.initials} color={a.color} src={a.avatar_url} size="xs" className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 text-[11.5px] text-muted-foreground">
          <span className="font-semibold text-foreground">{a.name || "Ukjent"}</span>
          {decision ? <span className="inline-flex items-center gap-1 rounded-[4px] bg-[hsl(var(--navy))] px-1 py-px text-[10px] font-semibold text-white"><Gavel className="h-2.5 w-2.5" /> Beslutning · ikke anvendt</span> : null}
          {showAnchor && m.anchor?.item_ref ? <span className="font-mono">{m.anchor.item_ref}</span> : null}
          <time className="ml-auto" dateTime={m.created_at}>{ago(m.created_at)}</time>
        </div>
        <div className="mt-0.5 whitespace-pre-wrap text-[13px] leading-[1.55] text-foreground [overflow-wrap:anywhere]">{m.content}</div>
        {m.mentions?.length ? <div className="mt-1 flex flex-wrap gap-1">{m.mentions.map((p) => <PersonChip key={p.id} person={p} size="xs" className="text-[11.5px]" />)}</div> : null}
      </div>
    </li>
  );
}

function StartThread({ item, ops, testId }) {
  return (
    <button type="button" onClick={() => ops.thread.start({ item_ids: [item.id] })} className="inline-flex h-8 items-center gap-1.5 rounded-full border border-dashed border-black/[0.18] px-3 text-[12.5px] font-medium text-foreground/75 transition-colors hover:border-[hsl(var(--navy)/0.5)] hover:bg-[hsl(var(--navy)/0.04)] hover:text-[hsl(var(--navy))]" title={`Åpner Meldinger med «Gjelder ${item.ref}» ferdig satt`} data-testid={testId}>
      <MessagesSquare className="h-3.5 w-3.5" /> Start samtale
    </button>
  );
}

/** Internal dialogue about one work item: the threads that mention it, opened in Meldinger; a new one starts here. Nothing here changes the calculation. */
export function ItemDialog({ item, project, ops, compact = false, bare = false }) {
  const { threads, notes } = useItemThreads(project, item);
  const [showLegacy, setShowLegacy] = useState(false);
  return (
    <div data-testid={`item-dialog-${item.ref}`}>
      {!bare ? (
        <SectionLabel>
          <span className="inline-flex items-center gap-1.5"><MessagesSquare className="h-3.5 w-3.5 text-muted-foreground" /> Samtaler om posten <span className="ml-1 font-mono text-[11px] font-normal text-muted-foreground">{threads.length}</span></span>
        </SectionLabel>
      ) : null}
      {!compact ? <p className="mt-1 text-[12px] text-muted-foreground">Teamets samtaler om akkurat denne posten – samme tråd som i Meldinger. Et «ja» her godkjenner ikke noe: endringer i kalkyle og tilbud gjøres eksplisitt og logges.</p> : null}
      {threads.length ? (
        <ul className={cn("space-y-1", !bare && "mt-2")} data-testid={`item-dialog-list-${item.ref}`}>
          {threads.map((t) => (
            <li key={t.id}>
              <button type="button" onClick={() => ops.thread.open(t.id)} className={cn("group flex w-full items-center gap-2.5 rounded-[10px] border px-2.5 py-2 text-left transition-colors hover:bg-black/[0.02]", t.unread ? "border-[hsl(var(--primary)/0.35)] bg-[hsl(var(--primary)/0.04)]" : "border-black/[0.06] bg-white")} data-testid={`item-dialog-thread-${t.id}`}>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2"><span className={cn("truncate text-[13px]", t.unread ? "font-semibold" : "font-medium")}>{t.subject}</span><ThreadStatus status={t.status} /></span>
                  <span className="mt-0.5 block truncate text-[12px] text-muted-foreground">{t.last_message?.author?.name ? `${t.last_message.author.name.split(" ")[0]}: ` : ""}{t.last_message?.preview}</span>
                </span>
                <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">{t.message_count}</span>
                <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50 group-hover:text-foreground" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {notes.length ? (
        <div className="mt-2">
          <button type="button" onClick={() => setShowLegacy((v) => !v)} className="text-[12px] text-muted-foreground hover:text-foreground" data-testid={`item-dialog-legacy-toggle-${item.ref}`}>{showLegacy ? "Skjul" : "Vis"} {notes.length} {notes.length === 1 ? "tidligere notat" : "tidligere notater"}</button>
          {showLegacy ? <ul className="mt-1.5 space-y-1.5">{notes.map((m) => <NoteLine key={m.id} m={m} showAnchor={m.anchor?.item_id !== item.id} testId={`item-dialog-note-${m.id}`} />)}</ul> : null}
        </div>
      ) : null}
      {ops.thread ? <div className="mt-2"><StartThread item={item} ops={ops} testId={`item-dialog-composer-${item.ref}`} /></div> : null}
    </div>
  );
}
